import { useState, useEffect, type FormEvent } from 'react';
import { canAccessProject, formatMoney } from '@vns/core';
import { useSession } from '../lib/session';
import { useLookups, todayLocal, normalizeUnitLabel } from '../lib/lookups';
import {
  useCreateTransferBatch,
  useProjectStock,
  emptyTransferLine,
  filledTransferCount,
  transferLineTotalPaise,
  transferLinesTotalPaise,
  validateTransfer,
  transferHasErrors,
  type TransferDraft,
  type TransferLine,
  type TransferErrors,
} from '../lib/stock-transfer-batches';
import { Drawer, Field, useToast, Select } from './ui';
import { Repeat, Truck, Plus, Trash2, Calendar, MapPin, Sparkles, IndianRupee } from 'lucide-react';

/**
 * Record an inter-site stock transfer trip with multiple material lines,
 * per-unit daily valuation rates, and total transfer value.
 */
export default function StockTransferDrawer({
  onClose,
  fromProjectId,
  fromProjectName,
}: {
  onClose: () => void;
  fromProjectId?: string;
  fromProjectName?: string;
}) {
  const actor = useSession((s) => s.actor);
  const { data: lk, isLoading } = useLookups();
  const create = useCreateTransferBatch();
  const toast = useToast();

  const [draft, setDraft] = useState<TransferDraft>({
    date: todayLocal(),
    from_project_id: fromProjectId ?? '',
    to_project_id: '',
    vehicle_no: '',
    driver_name: '',
    remarks: '',
    lines: [emptyTransferLine()],
  });
  const [errors, setErrors] = useState<TransferErrors>({ lines: {} });
  const [touched, setTouched] = useState(false);

  const { data: stockMap, isLoading: stockLoading } = useProjectStock(draft.from_project_id);

  const set = <K extends keyof TransferDraft>(k: K, v: TransferDraft[K]) => {
    const next = { ...draft, [k]: v };
    setDraft(next);
    if (touched) setErrors(validateTransfer(next, stockMap));
  };

  const patchLine = (i: number, patch: Partial<TransferLine>) => {
    const next: TransferDraft = {
      ...draft,
      lines: draft.lines.map((l, n) => (n === i ? { ...l, ...patch } : l)),
    };
    setDraft(next);
    if (touched) setErrors(validateTransfer(next, stockMap));
  };

  const addLine = () =>
    setDraft((d) => ({ ...d, lines: [...d.lines, emptyTransferLine()] }));

  const removeLine = (i: number) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.length > 1 ? d.lines.filter((_, n) => n !== i) : [emptyTransferLine()],
    }));

  const toOptions = lk?.projects.filter((p) => p.id !== draft.from_project_id);
  const fromOptions = lk?.projects.filter((p) => {
    if (p.id === draft.to_project_id) return false;
    return canAccessProject(actor, p.id);
  });

  useEffect(() => {
    const single = fromOptions?.length === 1 ? fromOptions[0] : undefined;
    if (!fromProjectId && !draft.from_project_id && single) {
      setDraft((d) => ({ ...d, from_project_id: single.id }));
    }
  }, [fromProjectId, fromOptions, draft.from_project_id]);

  useEffect(() => {
    if (touched) {
      setErrors(validateTransfer(draft, stockMap));
    }
  }, [stockMap, touched, draft]);

  const count = filledTransferCount(draft.lines);
  const totalValuePaise = transferLinesTotalPaise(draft.lines);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    const found = validateTransfer(draft, stockMap);
    setErrors(found);
    if (transferHasErrors(found)) return;

    try {
      await create.mutateAsync(draft);
      toast.show(`Transfer recorded — ${count} material${count === 1 ? '' : 's'}`);
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the transfer', true);
    }
  }

  return (
    <Drawer
      title={fromProjectId ? 'Transfer Material Out' : 'Add Stock Transfer'}
      description={
        fromProjectId
          ? `Dispatch several materials from ${fromProjectName ?? 'this site'} to another project with daily valuation rates`
          : 'Record inter-site dispatch of materials with per-unit transfer rates and amounts'
      }
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Save Transfer Dispatch"
      busy={create.isPending}
      wide
    >
      <div className="space-y-4">
        {/* Top Header Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-slate-50/70 p-3.5 sm:p-4 rounded-[10px] border border-slate-200/80">
          <Field label="Transfer Date">
            <input
              type="date"
              value={draft.date}
              onChange={(e) => set('date', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>

          <Field label="Vehicle No." optional>
            <input
              value={draft.vehicle_no}
              maxLength={20}
              onChange={(e) => set('vehicle_no', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
            />
          </Field>

          {!fromProjectId && (
            <Field label="Sending Site (From)" error={errors.from_project_id}>
              <Select
                value={draft.from_project_id}
                disabled={isLoading}
                placeholder={isLoading ? 'Loading…' : 'Choose sending site'}
                options={[
                  { value: '', label: isLoading ? 'Loading…' : 'Choose sending site' },
                  ...(fromOptions?.map((p) => ({ value: p.id, label: p.name })) ?? []),
                ]}
                className={errors.from_project_id ? 'border-rose-500' : ''}
                onChange={(val) => set('from_project_id', val)}
              />
            </Field>
          )}

          <Field label="Receiving Site (To)" error={errors.to_project_id}>
            <Select
              value={draft.to_project_id}
              disabled={isLoading}
              placeholder={isLoading ? 'Loading…' : 'Choose receiving site'}
              options={[
                { value: '', label: isLoading ? 'Loading…' : 'Choose receiving site' },
                ...(toOptions?.map((p) => ({ value: p.id, label: p.name })) ?? []),
              ]}
              className={errors.to_project_id ? 'border-rose-500' : ''}
              onChange={(val) => set('to_project_id', val)}
            />
          </Field>

          <Field label="Driver Name" optional span2>
            <input
              value={draft.driver_name}
              maxLength={80}
              onChange={(e) => set('driver_name', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>
        </div>

        {/* Multi-Row Material Transfer Table */}
        <div className="bg-white rounded-[10px] border border-slate-200/80 p-3.5 sm:p-4 space-y-3 shadow-xs">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-800 m-0 flex items-center gap-1.5">
                <Truck className="w-4 h-4 text-sky-600" />
                Materials & Daily Valuation Rates on this Trip
              </h4>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                Specify materials, moved quantities, units, per-unit rate (₹), and consignment notes. Total amounts calculate automatically.
              </p>
            </div>
            {errors.form && (
              <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2.5 py-0.5 rounded-[6px] border border-rose-200">
                {errors.form}
              </span>
            )}
          </div>

          <div className="bill-panel p-0 bg-transparent border-0">
            <div className="transfer-line-head hidden sm:grid">
              <div className="material">Material Item</div>
              <div className="qty text-center">Qty Moved</div>
              <div className="unit text-center">Unit</div>
              <div className="rate text-center">Rate (₹)</div>
              <div className="amount text-right">Total Value</div>
              <div className="note">Transfer Note</div>
              <div className="rm" />
            </div>

            <div className="bill-lines space-y-1.5 pt-1.5">
              {draft.lines.map((line, i) => {
                const le = errors.lines[i] ?? {};
                const amount = transferLineTotalPaise(line);
                return (
                  <div
                    key={i}
                    className="transfer-line items-center gap-2 bg-slate-50/50 p-1.5 sm:p-2 rounded-[8px] border border-slate-200/60 hover:bg-slate-50 transition-colors"
                  >
                    <div className="lineno text-xs font-bold text-slate-400">#{i + 1}</div>

                    {/* Material Selector */}
                    <div className="field material">
                      <Select
                        size="sm"
                        value={line.material_id}
                        disabled={isLoading}
                        placeholder={isLoading ? 'Loading…' : 'Choose a material'}
                        options={[
                          { value: '', label: isLoading ? 'Loading…' : 'Choose a material' },
                          ...(lk?.materials?.map((m) => {
                            const avail = draft.from_project_id && stockMap ? stockMap.get(m.id) ?? 0 : null;
                            const u = lk?.units.find((unit) => unit.id === m.unit_id)?.short_name ?? '';
                            const stockSuffix = avail !== null ? ` (${avail} ${u} in stock)` : '';
                            return { value: m.id, label: `${m.name}${stockSuffix}` };
                          }) ?? []),
                        ]}
                        className={le.material_id ? 'border-rose-500' : ''}
                        onChange={(val) => {
                          const m = lk?.materials.find((item) => item.id === val);
                          patchLine(i, {
                            material_id: val,
                            unit_id: m?.unit_id || line.unit_id,
                            rateRupees: line.rateRupees || (m?.rate_paise ? String(m.rate_paise / 100) : ''),
                          });
                        }}
                      />
                      {le.material_id && <div className="text-[10px] text-rose-600 mt-0.5">{le.material_id}</div>}
                      {line.material_id && draft.from_project_id && (
                        <div className="text-[10.5px] mt-0.5 flex items-center gap-1">
                          {stockLoading ? (
                            <span className="text-slate-400">Checking site stock…</span>
                          ) : (() => {
                            const avail = stockMap?.get(line.material_id) ?? 0;
                            const m = lk?.materials.find((item) => item.id === line.material_id);
                            const u = lk?.units.find((unit) => unit.id === (line.unit_id || m?.unit_id))?.short_name ?? '';
                            if (avail <= 0) {
                              return (
                                <span className="text-rose-600 font-semibold">
                                  Out of stock at this site (0 {u})
                                </span>
                              );
                            }
                            return (
                              <span className="text-emerald-700 font-semibold">
                                In stock at site: {avail} {u}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Quantity */}
                    <div className="field qty">
                      <input
                        id={`tq-${i}`}
                        value={line.quantity}
                        inputMode="decimal"
                        aria-invalid={!!le.quantity}
                        onChange={(e) => patchLine(i, { quantity: e.target.value })}
                        className={`w-full px-2.5 py-1.5 text-xs text-center bg-white border ${
                          le.quantity ? 'border-rose-500 focus:ring-rose-500' : 'border-slate-200/80 focus:ring-sky-500'
                        } rounded-[6px] focus:outline-none focus:ring-1 text-slate-800 font-mono font-semibold`}
                      />
                      {le.quantity && (
                        <div className="text-[10px] text-rose-600 font-semibold mt-0.5 whitespace-nowrap">
                          {le.quantity}
                        </div>
                      )}
                    </div>

                    {/* Unit */}
                    <div className="field unit">
                      <Select
                        size="sm"
                        value={line.unit_id}
                        disabled={isLoading}
                        placeholder="—"
                        options={[
                          { value: '', label: '—' },
                          ...(lk?.units?.map((u) => ({ value: u.id, label: normalizeUnitLabel(u.name, u.short_name) })) ?? []),
                        ]}
                        onChange={(val) => patchLine(i, { unit_id: val })}
                      />
                    </div>

                    {/* Rate per Unit (₹) */}
                    <div className="field rate">
                      <input
                        id={`tr-${i}`}
                        value={line.rateRupees}
                        inputMode="decimal"
                        aria-invalid={!!le.rateRupees}
                        onChange={(e) => patchLine(i, { rateRupees: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs text-center bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 font-mono font-semibold"
                      />
                      {le.rateRupees && <div className="text-[10px] text-rose-600 mt-0.5">{le.rateRupees}</div>}
                    </div>

                    {/* Total Line Amount */}
                    <div className={`amount ${amount === null ? 'empty' : ''}`}>
                      <div className="amount-val">
                        {amount === null ? '—' : formatMoney(amount)}
                      </div>
                    </div>

                    {/* Note */}
                    <div className="field note">
                      <input
                        id={`tn-${i}`}
                        value={line.remarks}
                        maxLength={120}
                        onChange={(e) => patchLine(i, { remarks: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800"
                      />
                    </div>

                    {/* Remove Line */}
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      title="Remove this transfer line"
                      aria-label={`Remove line ${i + 1}`}
                      className="btn-remove-line rm"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addLine}
              className="btn-add-line"
            >
              <Plus size={14} strokeWidth={2.2} />
              <span>Add Another Material Moved</span>
            </button>
          </div>
        </div>

        {/* Totals Summary Banner */}
        <div className="drawer-summary-banner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <span className="title">
            {count} material{count === 1 ? '' : 's'} dispatched • {draft.vehicle_no.trim() ? `Vehicle: ${draft.vehicle_no}` : 'Consignment in Transit'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>Total Transfer Value:</span>
            <span className="amount" style={{ color: 'var(--brand)', fontWeight: 700, fontSize: 14 }}>
              {formatMoney(totalValuePaise)}
            </span>
          </div>
        </div>

        {/* General Remarks */}
        <div>
          <Field label="Trip / Waybill Remarks" optional span2>
            <textarea
              rows={2}
              value={draft.remarks}
              maxLength={300}
              onChange={(e) => set('remarks', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>
        </div>
      </div>
    </Drawer>
  );
}
